import axios from "axios";
import { toast } from "react-toastify";
import {
  clearStoredSession,
  getApiBaseUrl,
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredSession,
} from "../data/authStorage";

const apiBaseUrl = getApiBaseUrl();
const publicAuthEndpointPattern = /\/auth\/organization\/(login|refresh)(?:\?|$)/;

let refreshPromise = null;
const mutationMethods = new Set(["post", "put", "patch", "delete"]);

const isPublicAuthEndpoint = (url = "") => publicAuthEndpointPattern.test(url);

const getUserFacingErrorMessage = (error) => {
  if (error?.response?.status === 413 && /\/profile-photo(?:\?|$)/.test(error?.config?.url || "")) {
    return "Profile picture is too large. Please choose an image that is 10 MB or smaller.";
  }

  const rawMessage = error?.response?.data?.message || error?.response?.data || error?.message || "Unable to complete this action.";
  const message = Array.isArray(rawMessage) ? rawMessage.join(", ") : String(rawMessage);
  if (/<(?:!doctype|html|head|body|center|title|h[1-6])\b/i.test(message)) {
    const refunded = /\brefunded?\b/i.test(message.replace(/<[^>]*>/g, " "));
    return refunded
      ? "The service is temporarily unavailable. Your wallet has been refunded."
      : "The service is temporarily unavailable. Please try again shortly.";
  }
  return message;
};

const makeSessionErrorUserSafe = (error) => {
  if (error?.response?.data && typeof error.response.data === "object") {
    error.response.data = {
      ...error.response.data,
      message: "Your session has expired. Please sign in again.",
    };
  }
  return error;
};

axios.interceptors.request.use((config) => {
  const accessToken = getStoredAccessToken();
  const requestUrl = config.url || "";

  if (accessToken && !isPublicAuthEndpoint(requestUrl)) {
    config.headers = {
      ...(config.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const requestUrl = originalRequest?.url || "";
    const userMessage = status !== 401 ? getUserFacingErrorMessage(error) : null;
    if (userMessage && error?.response) {
      if (error.response.data && typeof error.response.data === "object") {
        error.response.data.message = userMessage;
      } else {
        error.response.data = { message: userMessage };
      }
    }

    if (status !== 401 || originalRequest?._retry || isPublicAuthEndpoint(requestUrl)) {
      const method = `${originalRequest?.method || ""}`.toLowerCase();
      if (status !== 401 && mutationMethods.has(method) && !originalRequest?.suppressGlobalErrorToast) {

        toast.error(userMessage, { toastId: `api-error:${method}:${requestUrl}:${userMessage}` });
      }
      return Promise.reject(status === 401 && originalRequest?._retry
        ? makeSessionErrorUserSafe(error)
        : error);
    }

    const refreshToken = getStoredRefreshToken();

    if (!refreshToken || !apiBaseUrl) {
      clearStoredSession();
      // Dispatch custom event to notify app of session expiration
      window.dispatchEvent(new CustomEvent("sessionExpired"));
      return Promise.reject(makeSessionErrorUserSafe(error));
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${apiBaseUrl}/auth/organization/refresh`, { refreshToken })
          .then((response) => {
            const payload = response?.data || {};

            setStoredSession({
              accessToken: payload.accessToken,
              refreshToken: payload.refreshToken,
              organization: payload.organization,
            });

            return payload;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      const session = await refreshPromise;

      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${session.accessToken}`,
      };

      return axios(originalRequest);
    } catch (refreshError) {
      clearStoredSession();
      // Dispatch custom event to notify app of session expiration
      window.dispatchEvent(new CustomEvent("sessionExpired"));
      return Promise.reject(makeSessionErrorUserSafe(refreshError));
    }
  }
);

export default axios;
